import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { Col, Row, Modal, Button, Tabs, Input, Table, Divider, Tooltip, Space, Popconfirm } from "antd";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { faEdit, faPlay, faRedo, faStop, faTrash, faUndo } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import WindowedSelect from "react-windowed-select";
import ModalAddTimeEntry from "./ModalAddTimeEntry";
dayjs.extend(duration);

function TimerModal(props) {
  //Timer Modal Settings
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  const [timerModalWidth, setTimerModalWidth] = useState(1400);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [jobTasks, setJobTasks] = useState([]);
  const [selectedJobTask, setSelectedJobTask] = useState(null);
  const [timerComments, setTimerComments] = useState("");
  const [timerEntries, setTimerEntries] = useState([]);
  const [activeTimerEntry, setActiveTimerEntry] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [debounceTimeoutReference, setDebounceTimeoutReference] = useState(null);
  const [loadingActiveTimerEntry, setLoadingActiveTimerEntry] = useState(false);

  const [addTimeEntryProps, setAddTimeEntryProps] = useState({
    open: false,
    job_id: null,
    task_id: null,
    client_id: null,
    refresh: false,
    timer_entries: [],
  });

  const OnSelectTimerEntryChange = async (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    let selected_timer_entries = timerEntries.filter((f) => newSelectedRowKeys.includes(f.id));
    setAddTimeEntryProps({
      ...addTimeEntryProps,
      timer_entries: selected_timer_entries,
      client_id: selected_timer_entries ? selected_timer_entries[0]?.clients_xref : null,
      job_id: selected_timer_entries ? selected_timer_entries[0]?.jobs_xref : null,
      task_id: selected_timer_entries ? selected_timer_entries[0]?.tasks_xref : null,
    });
  };
  const GetClients = async () => {
    let request = await props.AxiosRequest("/api/clients/get/all");
    setClients(
      request.data.clients.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };
  const GetJobTasks = async () => {
    let request = await props.AxiosRequest("/api/tasks/get/by-client", { cid: selectedClient.id });
    setJobTasks(request.data.tasks);
    setLoadingActiveTimerEntry(true);
  };
  const GetTimerEntries = async () => {
    let response = await props.AxiosRequest("/api/timer-entries/get/open");
    setTimerEntries(
      response.data.timer_entries
        .filter((f) => f.status !== "started")
        .map((m) => {
          return { ...m, key: m.id };
        }),
    );
    let active_timer = response.data.timer_entries.find((f) => f.status === "started");
    setActiveTimerEntry(active_timer);
    setLoadingActiveTimerEntry(true);
  };
  const StartTimer = async () => {
    await props.AxiosRequest("/api/timer-entries/start", {
      clients_xref: selectedClient?.id,
      jobs_xref: selectedJobTask?.jobs_xref,
      tasks_xref: selectedJobTask?.tasks_xref,
      comments: timerComments,
      start_time: dayjs(),
    });
    GetTimerEntries();
  };
  const ResumeTimer = async ({ clients_id, jobs_id, tasks_id }) => {
    if (activeTimerEntry) return props.MakeNotification("error", "There is already a timer running.");
    await props.AxiosRequest("/api/timer-entries/start", {
      clients_xref: clients_id,
      jobs_xref: jobs_id,
      tasks_xref: tasks_id,
      start_time: dayjs(),
    });
    GetTimerEntries();
  };
  const UpdateTimer = async () => {
    await props.AxiosRequest("/api/timer-entries/update", { ...activeTimerEntry }, true);
    GetTimerEntries();
  };
  const StopTimer = async () => {
    await props.AxiosRequest("/api/timer-entries/stop", { ...activeTimerEntry, end_time: dayjs() });

    setSelectedClient(null);
    setSelectedJobTask(null);
    setTimerComments("");
    GetTimerEntries();
  };
  const DeleteTimerEntry = async (id) => {
    await props.AxiosRequest("/api/timer-entries/delete", { id });
    GetTimerEntries();
  };
  const UpdateClientToReflectActiveTimer = async () => {
    setSelectedClient(clients.find((f) => f.id === activeTimerEntry?.clients_xref));
  };
  const UpdateJobTaskToReflectActiveTimer = async () => {
    let job_tasks = jobTasks.map((m) => {
      return { ...m, label: m.job_name + " > " + m.description, value: m.job_id + "-" + m.task_id };
    });
    setSelectedJobTask(job_tasks.find((f) => f.id === activeTimerEntry?.tasks_xref));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: OnSelectTimerEntryChange,
  };
  const timer_entries_columns = [
    {
      title: "Client",
      dataIndex: "clients_name",
      key: "clients_name",
      render: (value) => (value ? value : "Not Set"),
      sorter: (a, b) => (a.status.toUpperCase() < b.status.toUpperCase() ? -1 : a.status.toUpperCase() > b.status.toUpperCase() ? 1 : 0),
    },
    {
      title: "Job",
      dataIndex: "jobs_name",
      key: "jobs_name",
      render: (value) => (value ? value : "Not Set"),
      sorter: (a, b) => (a.invoiced_at.toUpperCase() < b.invoiced_at.toUpperCase() ? -1 : a.invoiced_at.toUpperCase() > b.invoiced_at.toUpperCase() ? 1 : 0),
    },
    {
      title: "Task",
      dataIndex: "tasks_name",
      key: "tasks_name",
      render: (value) => (value ? value : "Not Set"),
      sorter: (a, b) => (a.tasks_name.toUpperCase() < b.tasks_name.toUpperCase() ? -1 : a.tasks_name.toUpperCase() > b.tasks_name.toUpperCase() ? 1 : 0),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      render: (value) => <Input.TextArea value={value} rows={1} />,
    },
    {
      title: "Time Spent",
      dataIndex: "seconds_elapsed",
      key: "seconds_elapsed",
      align: "right",
      render: (value) => dayjs.duration(value * 1000).format("HH:mm:ss"),
      sorter: (a, b) => (a.seconds_elapsed < b.seconds_elapsed ? -1 : a.seconds_elapsed > b.seconds_elapsed ? 1 : 0),
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (id, row) => (
        <Space>
          <Tooltip title="Start another time entry with the same client, job, and task">
            <Button
              size="small"
              type="primary"
              onClick={() => {
                ResumeTimer({ clients_id: row.clients_xref, jobs_id: row.jobs_xref, tasks_id: row.tasks_xref });
              }}
            >
              <FontAwesomeIcon icon={faUndo} size="sm" className="mr-2" />
              Continue
            </Button>
          </Tooltip>
          {/* <Button
            size="small"
            onClick={() => {
              ResumeTimer({ clients_id: row.clients_xref, jobs_id: row.jobs_xref, tasks_id: row.tasks_xref });
            }}
          >
            <FontAwesomeIcon icon={faEdit} size="sm" className="mr-2" /> Edit
          </Button> */}
          <Popconfirm
            title="Delete the timer entry"
            description="Are you sure to delete this timer entry?"
            onConfirm={() => {
              DeleteTimerEntry(id);
            }}
          >
            <Button size="small" danger type="primary">
              <FontAwesomeIcon icon={faTrash} size="sm" className="mr-2" /> Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  //onmount
  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(1400, window.innerWidth);
      setTimerModalWidth(maxSize);
    };
    window.addEventListener("resize", resize);
    GetTimerEntries();
  }, []);
  useEffect(() => {
    if (props.open) {
      setTimerModalOpen(true);
      GetClients();
      GetTimerEntries();
    }
  }, [props.open]);
  useEffect(() => {
    props.setTimerModalOpen(timerModalOpen);
  }, [timerModalOpen]);
  useEffect(() => {
    if (selectedClient) GetJobTasks();
  }, [selectedClient]);
  useEffect(() => {
    if (activeTimerEntry?.updated) {
      clearTimeout(debounceTimeoutReference);
      let ref = setTimeout(() => {
        UpdateTimer();
      }, 500);
      setDebounceTimeoutReference(ref);
      setActiveTimerEntry({ ...activeTimerEntry, updated: false });
    }
  }, [activeTimerEntry]);
  useEffect(() => {
    //
    if (activeTimerEntry) props.setStopwatchPulse(true);
    if (!activeTimerEntry) props.setStopwatchPulse(false);
  }, [activeTimerEntry]);
  useEffect(() => {
    if (loadingActiveTimerEntry) {
      if (activeTimerEntry) UpdateClientToReflectActiveTimer();
      if (selectedClient && activeTimerEntry && jobTasks && activeTimerEntry?.tasks_xref) UpdateJobTaskToReflectActiveTimer();
      setLoadingActiveTimerEntry(false);
    }
  }, [selectedClient, activeTimerEntry, jobTasks, loadingActiveTimerEntry]);

  useEffect(() => {
    if (addTimeEntryProps.refresh) {
      GetTimerEntries();
    }
  }, [addTimeEntryProps.refresh]);

  return (
    <>
      <Modal
        width={timerModalWidth}
        footer={
          <Space>
            <Button
              onClick={() => {
                setTimerModalOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setAddTimeEntryProps({ ...addTimeEntryProps, open: true });
              }}
            >
              Add A Time Entry
            </Button>
          </Space>
        }
        title={<div>FirmTamer Timer</div>}
        open={timerModalOpen}
        onCancel={() => {
          setTimerModalOpen(false);
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={4}>
            {activeTimerEntry ? (
              <Button
                className="form-control btn-danger"
                onClick={() => {
                  StopTimer();
                }}
              >
                <FontAwesomeIcon icon={faStop} className="mr-2" /> Stop Timer
              </Button>
            ) : (
              <Button
                className="form-control btn-success"
                onClick={() => {
                  StartTimer();
                }}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" /> Start Timer
              </Button>
            )}
          </Col>
          <Col span={4}>
            <TimerModalSecondsCounter startTime={activeTimerEntry?.start_time || null} />
          </Col>
          <Col span={16}></Col>
          <Col span={8}>
            <label className="col-form-label-sm">Client</label>
            <WindowedSelect
              options={props.MakeClientOptions(clients)}
              value={selectedClient}
              onChange={(e) => {
                if (selectedClient !== e) {
                  setJobTasks([]);
                  setSelectedJobTask(null);
                  setSelectedClient(e);
                  if (activeTimerEntry) setActiveTimerEntry({ ...activeTimerEntry, clients_xref: e.id, jobs_xref: null, tasks_xref: null, updated: true });
                }
              }}
            />
          </Col>
          <Col span={8}>
            <label className="col-form-label-sm">Job / Task</label>
            <Select
              options={props.MakeJobTaskOptions(jobTasks)}
              value={selectedJobTask}
              onChange={(e) => {
                setSelectedJobTask(e);
                if (activeTimerEntry) setActiveTimerEntry({ ...activeTimerEntry, jobs_xref: e.jobs_xref, tasks_xref: e.tasks_xref, updated: true });
              }}
            />
          </Col>
          <Col span={8}>
            <label className="col-form-label-sm">Comments</label>
            <Input.TextArea
              rows="1"
              style={{ lineHeight: "23px" }}
              value={timerComments}
              onChange={(e) => {
                setTimerComments(e.target.value);
                if (activeTimerEntry) setActiveTimerEntry({ ...activeTimerEntry, comments: e.target.value, updated: true });
              }}
            />
          </Col>
          <Col span={24}>
            <Divider orientation="left" plain>
              Timer Entries
            </Divider>
          </Col>
          <Col span={24}>
            <Table rowSelection={rowSelection} columns={timer_entries_columns} dataSource={timerEntries}></Table>
          </Col>
        </Row>
      </Modal>
      <ModalAddTimeEntry {...props} addTimeEntryProps={addTimeEntryProps} setAddTimeEntryProps={setAddTimeEntryProps} />
    </>
  );
}

function TimerModalSecondsCounter(props) {
  const [seconds, setSeconds] = useState(0);
  const [intervalReference, setIntervalReference] = useState(null);

  const UpdateSeconds = async () => {
    let seconds_elapsed = dayjs().diff(dayjs(props?.startTime), "seconds");
    setSeconds(seconds_elapsed);
  };
  useEffect(() => {
    if (props?.startTime) {
      let ref = setInterval(() => {
        UpdateSeconds();
      }, 500);
      clearInterval(intervalReference);
      setIntervalReference(ref);
    }
    if (!props?.startTime) {
      clearInterval(intervalReference);
      setIntervalReference(null);
      setSeconds(0);
    }
  }, [props?.startTime]);

  return (
    <>
      <Input className="form-control text-center" value={dayjs.duration(seconds * 1000).format("HH:mm:ss")} readOnly />
    </>
  );
}

export default TimerModal;
